/**
 * Submission Status Card
 * 
 * Enhanced card for displaying submission status in the submitter journey.
 * Shows clear visual indicators of the current persisted outcome.
 */

import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Hammer,
  Target,
  Trophy,
  Play,
  Pause,
  Archive,
  Link as LinkIcon,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// ============================================================================
// Submission Status Types
// ============================================================================

/**
 * Comprehensive submission statuses covering the full lifecycle
 */
export type SubmissionStatus = 
  | 'SUBMITTED'           // Just submitted, awaiting intake
  | 'UNDER_REVIEW'        // Being reviewed by triage
  | 'BEING_SHAPED'        // Continued item work is underway
  | 'REDIRECTED'          // Routed to a different queue/team
  | 'NEEDS_CLARIFICATION' // Returned with questions
  | 'IN_PRIORITIZATION'   // Added to a prioritization session
  | 'PRIORITIZED'         // Has been ranked
  | 'COMMITTED'           // Above the cut line, committed to work
  | 'IN_PROGRESS'         // Being worked on
  | 'COMPLETED'           // Done
  | 'DEFERRED'            // Below the cut line
  | 'REJECTED';           // Not going to happen

export interface SubmissionStatusConfig {
  label: string;
  description: string;
  icon: typeof Clock;
  iconClassName: string;
  badgeClassName: string;
  phase: 'intake' | 'shaping' | 'prioritization' | 'execution' | 'terminal';
}

export const SUBMISSION_STATUS_CONFIG: Record<SubmissionStatus, SubmissionStatusConfig> = {
  SUBMITTED: {
    label: 'Submitted',
    description: 'Your idea has been received and is awaiting review',
    icon: Clock,
    iconClassName: 'text-gray-500',
    badgeClassName: 'bg-gray-100 text-gray-700',
    phase: 'intake',
  },
  UNDER_REVIEW: {
    label: 'Under Review',
    description: 'Your idea is being reviewed to determine next steps',
    icon: AlertCircle,
    iconClassName: 'text-yellow-500',
    badgeClassName: 'bg-yellow-100 text-yellow-700',
    phase: 'intake',
  },
  BEING_SHAPED: {
    label: 'Item Work Underway',
    description: 'Work is continuing on the item so it can be used where conditions are met',
    icon: Hammer,
    iconClassName: 'text-blue-500',
    badgeClassName: 'bg-blue-100 text-blue-700',
    phase: 'shaping',
  },
  REDIRECTED: {
    label: 'Redirected',
    description: 'This item was routed to a different queue for follow-up',
    icon: ExternalLink,
    iconClassName: 'text-sky-600',
    badgeClassName: 'bg-sky-100 text-sky-700',
    phase: 'intake',
  },
  NEEDS_CLARIFICATION: {
    label: 'Needs Clarification',
    description: 'We have questions about your idea - please respond',
    icon: AlertCircle,
    iconClassName: 'text-orange-500',
    badgeClassName: 'bg-orange-100 text-orange-700 ring-2 ring-orange-300',
    phase: 'shaping',
  },
  IN_PRIORITIZATION: {
    label: 'In Prioritization',
    description: 'Your idea is being evaluated against other priorities',
    icon: Target,
    iconClassName: 'text-purple-500',
    badgeClassName: 'bg-purple-100 text-purple-700',
    phase: 'prioritization',
  },
  PRIORITIZED: {
    label: 'Prioritized',
    description: 'Your idea has been ranked - see where it landed',
    icon: CheckCircle,
    iconClassName: 'text-indigo-500',
    badgeClassName: 'bg-indigo-100 text-indigo-700',
    phase: 'prioritization',
  },
  COMMITTED: {
    label: 'Committed',
    description: 'Your idea is above the cut line and will be worked on',
    icon: Trophy,
    iconClassName: 'text-green-500',
    badgeClassName: 'bg-green-100 text-green-700',
    phase: 'execution',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    description: 'Work has started on your idea',
    icon: Play,
    iconClassName: 'text-green-600',
    badgeClassName: 'bg-green-100 text-green-700',
    phase: 'execution',
  },
  COMPLETED: {
    label: 'Completed',
    description: 'Your idea has been delivered',
    icon: CheckCircle,
    iconClassName: 'text-green-600',
    badgeClassName: 'bg-green-100 text-green-800',
    phase: 'terminal',
  },
  DEFERRED: {
    label: 'Deferred',
    description: 'Your idea is below the cut line for now',
    icon: Pause,
    iconClassName: 'text-gray-500',
    badgeClassName: 'bg-gray-100 text-gray-600',
    phase: 'terminal',
  },
  REJECTED: {
    label: 'Not Proceeding',
    description: 'This idea will not be pursued at this time',
    icon: XCircle,
    iconClassName: 'text-red-500',
    badgeClassName: 'bg-red-100 text-red-700',
    phase: 'terminal',
  },
};

// ============================================================================
// Component Props
// ============================================================================

export interface SubmissionStatusCardProps {
  submissionId: string;
  title: string;
  description?: string;
  status: SubmissionStatus;
  submittedAt: Date | string;
  
  // Optional linking
  itemId?: string;          // If promoted to item (item id)
  itemPoolId?: string;       // If prioritized (pool/list id)
  rank?: number;             // Rank in session result
  aboveTheLine?: boolean;    // Is it committed?
  
  // Similar items indicator
  relatedItemCount?: number;
  
  // Actions
  onViewDetails?: () => void;
  onRespondToClarification?: () => void;
  
  // Display options
  compact?: boolean;
  /** Row mode: renders as a flat row inside a parent divided list container (no outer border/shadow) */
  row?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function SubmissionStatusCard({
  submissionId,
  title,
  description,
  status,
  submittedAt,
  itemId,
  itemPoolId,
  rank,
  aboveTheLine,
  relatedItemCount,
  onViewDetails,
  onRespondToClarification,
  compact = false,
  row = false,
}: SubmissionStatusCardProps) {
  const config = SUBMISSION_STATUS_CONFIG[status];
  const Icon = config.icon;
  
  const formattedDate = typeof submittedAt === 'string' 
    ? new Date(submittedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : submittedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  // ── Compact (pill-style for widgets) ────────────────────────────────────────
  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 border border-neutral-200 rounded-xl hover:shadow-sm transition-shadow bg-white">
        <Icon className={`h-5 w-5 ${config.iconClassName}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-neutral-900 truncate">{title}</span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.badgeClassName}`}>
              {config.label}
            </span>
          </div>
        </div>
        {itemId && (
          <Link 
            to={`/prioritization/items/${itemId}`}
            className="p-1 text-neutral-400 hover:text-primary-600"
          >
            <ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </div>
    );
  }

  // ── Row (inside a divide-y list container) ───────────────────────────────────
  if (row) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/60 transition-colors">
        {/* Status icon */}
        <div className={`p-1.5 rounded-lg bg-gray-50 flex-shrink-0`}>
          <Icon className={`h-3.5 w-3.5 ${config.iconClassName}`} />
        </div>

        {/* Title + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 truncate">{title}</span>
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${config.badgeClassName}`}>
              {config.label}
            </span>
            {rank !== undefined && (
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                aboveTheLine ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                #{rank} {aboveTheLine ? '· Committed' : '· Below line'}
              </span>
            )}
          </div>
          {description && (
            <p className="text-xs text-gray-400 truncate mt-0.5">{description}</p>
          )}
        </div>

        {/* Meta + actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-gray-400 hidden sm:block">{formattedDate}</span>

          {status === 'NEEDS_CLARIFICATION' && onRespondToClarification && (
            <button
              onClick={onRespondToClarification}
              className="px-2.5 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              Respond
            </button>
          )}

          {itemId && (
            <Link
              to={`/prioritization/items/${itemId}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
            >
              View Item
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}

          {itemPoolId && rank !== undefined && (
            <Link
              to={`/prioritization/lists/${itemPoolId}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
            >
              In List
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}

          {!itemId && (
            <ChevronRight className="h-4 w-4 text-gray-300" />
          )}
        </div>
      </div>
    );
  }

  // ── Full card (standalone) ───────────────────────────────────────────────────
  return (
    <div className="p-4 border border-gray-200 rounded-xl bg-white hover:bg-gray-50/40 transition-colors">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-gray-50 flex-shrink-0">
          <Icon className={`h-5 w-5 ${config.iconClassName}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${config.badgeClassName}`}>
              {config.label}
            </span>
            {rank !== undefined && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                aboveTheLine ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                #{rank} {aboveTheLine ? '(Committed)' : '(Below line)'}
              </span>
            )}
          </div>
          
          {description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{description}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">{config.description}</p>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{formattedDate}</span>
          {itemId && (
            <span className="flex items-center gap-1">
              <LinkIcon className="h-3 w-3" />
              Linked to item
            </span>
          )}
          {relatedItemCount && relatedItemCount > 0 && (
            <span className="text-blue-600">
              {relatedItemCount} similar request{relatedItemCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {status === 'NEEDS_CLARIFICATION' && onRespondToClarification && (
            <button
              onClick={onRespondToClarification}
              className="px-2.5 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              Respond
            </button>
          )}
          {itemId && (
            <Link
              to={`/prioritization/items/${itemId}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
            >
              View Item <ExternalLink className="h-3 w-3" />
            </Link>
          )}
          {itemPoolId && rank !== undefined && (
            <Link
              to={`/prioritization/lists/${itemPoolId}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
            >
              View in List <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Status Filter Component
// ============================================================================

export interface StatusFilterProps {
  selectedStatuses: SubmissionStatus[];
  onToggle: (status: SubmissionStatus) => void;
  counts?: Partial<Record<SubmissionStatus, number>>;
}

export function SubmissionStatusFilter({
  selectedStatuses,
  onToggle,
  counts = {},
}: StatusFilterProps) {
  const phases = ['intake', 'shaping', 'prioritization', 'execution', 'terminal'] as const;
  
  const statusesByPhase = phases.reduce((acc, phase) => {
    acc[phase] = Object.entries(SUBMISSION_STATUS_CONFIG)
      .filter(([_, config]) => config.phase === phase)
      .map(([status]) => status as SubmissionStatus);
    return acc;
  }, {} as Record<typeof phases[number], SubmissionStatus[]>);
  
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(statusesByPhase).map(([phase, statuses]) => (
        <div key={phase} className="flex gap-1">
          {statuses.map((status) => {
            const config = SUBMISSION_STATUS_CONFIG[status];
            const isSelected = selectedStatuses.includes(status);
            const count = counts[status] || 0;
            
            return (
              <button
                key={status}
                onClick={() => onToggle(status)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                  isSelected ? config.badgeClassName : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                }`}
              >
                {config.label}
                {count > 0 && (
                  <span className="px-1.5 py-0.5 bg-white/50 rounded-full text-[10px]">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default SubmissionStatusCard;
